import { ArrowRight, Plus, Trash2 } from 'lucide-react';
import { getIcon } from '../../../../../../common/src/lib/icon-map';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';

interface InfoBadge {
  iconName: string;
  title: string;
  subtitle: string;
  order: number;
}

interface BadgeMarketingEditorProps {
  badges: InfoBadge[];
  onChange: (badges: InfoBadge[]) => void;
}

export function BadgeMarketingEditor({ badges, onChange }: BadgeMarketingEditorProps) {
  const addBadge = () => {
    onChange([
      ...badges,
      {
        iconName: 'Sparkles',
        title: 'Avantage Premium',
        subtitle: "Description de l'avantage",
        order: badges.length + 1,
      },
    ]);
  };

  const updateBadge = (idx: number, patch: Partial<InfoBadge>) => {
    const next = [...badges];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const removeBadge = (idx: number) => {
    onChange(badges.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4">
      {badges.map((badge, idx) => {
        const IconComponent = getIcon(badge.iconName);
        return (
          <div
            key={idx}
            className="group flex items-center gap-6 bg-white border-2 border-gray-100 rounded-3xl p-5 hover:border-[#00758d]/30 transition-all shadow-sm"
          >
            {/* Visual Preview part (Fixed size) */}
            <div className="flex flex-col items-center justify-center w-32 h-32 rounded-2xl bg-[#00758d]/5 border border-[#00758d]/10 flex-shrink-0 text-[#00758d]">
              <IconComponent size={32} className="mb-2" />
              <div className="text-[10px] font-black uppercase tracking-tighter opacity-40">Aperçu Icône</div>
            </div>

            {/* Editing part (Flexible) */}
            <div className="flex-1 grid grid-cols-1 gap-4">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                    Titre Principal (Grand)
                  </Label>
                  <ArrowRight size={10} className="text-gray-300" />
                </div>
                <Input
                  value={badge.title}
                  onChange={(e) => updateBadge(idx, { title: e.target.value })}
                  className="h-12 py-0 text-lg font-black text-[#00303c] border-gray-200 rounded-2xl focus:border-[#00758d] focus:ring-4 focus:ring-[#00758d]/5"
                  placeholder="Ex: Réservation instantanée"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                    Sous-titre explicatif
                  </Label>
                  <ArrowRight size={10} className="text-gray-300" />
                </div>
                <Input
                  value={badge.subtitle}
                  onChange={(e) => updateBadge(idx, { subtitle: e.target.value })}
                  className="h-10 py-0 text-sm font-semibold text-gray-500 border-gray-200 rounded-xl focus:border-[#00758d] focus:ring-4 focus:ring-[#00758d]/5"
                  placeholder="Ex: Confirmation immédiate"
                />
              </div>
            </div>

            {/* Actions */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeBadge(idx)}
              className="w-12 h-12 rounded-2xl text-red-300 hover:text-red-600 hover:bg-red-50 transition-all self-start"
            >
              <Trash2 size={20} />
            </Button>
          </div>
        );
      })}

      {badges.length < 3 && (
        <button
          type="button"
          onClick={addBadge}
          className="w-full flex items-center justify-center gap-3 border-2 border-dashed border-gray-200 rounded-3xl py-10 text-gray-400 hover:border-[#00758d]/30 hover:bg-[#00758d]/5 transition-all group"
        >
          <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-[#00758d]/10 transition-colors">
            <Plus size={24} className="group-hover:scale-110 group-hover:text-[#00758d] transition-all" />
          </div>
          <span className="text-sm font-black uppercase tracking-widest">Ajouter une bannière d'information</span>
        </button>
      )}
    </div>
  );
}
